import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import {
  useLazyGetBrandAccountsQuery,
  useLazyGetBrandCategoriesQuery,
  useGetBrandByIdQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
} from '../../store/api';
import { extractList } from '../../utils/response';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';
import { useServerValidationErrors } from '../../hooks/useServerValidationErrors';
import { toast } from 'react-toastify';
import { buildBrandRelationParams } from './brandApiHelpers';

const buildFormData = (values, { isEdit = false } = {}) => {
  const fd = new FormData();
  if (isEdit) fd.append('_method', 'PUT');
  fd.append('payment_account_id', values.payment_account_id);
  fd.append('brand_category_id', values.brand_category_id);
  fd.append('name', values.name ?? '');
  fd.append('type', values.type ?? '');
  fd.append('url', values.url ?? '');
  fd.append('address', values.address ?? '');
  fd.append('invoice_limit', String(values.invoice_limit ?? ''));
  fd.append('status', Number(values.status ?? 1));
  return fd;
};

const accountLabel = (a) =>
  a?.descriptor ||
  a?.label ||
  a?.name ||
  a?.account_name ||
  a?.title ||
  String(a?.id ?? '');
const categoryLabel = (c) => c?.name ?? c?.title ?? String(c?.id ?? '');

/** Resolve payment account from brand detail (API: payment_accounts[]). */
const resolvePaymentAccount = (b) => {
  if (!b || typeof b !== 'object') return null;
  if (Array.isArray(b.payment_accounts) && b.payment_accounts[0]) {
    return b.payment_accounts[0];
  }
  if (Array.isArray(b.paymentAccounts) && b.paymentAccounts[0]) {
    return b.paymentAccounts[0];
  }
  if (b.paymentAccount && typeof b.paymentAccount === 'object') {
    return b.paymentAccount;
  }
  if (b.payment_account && typeof b.payment_account === 'object') {
    return b.payment_account;
  }
  if (Array.isArray(b.brand_payment_accounts)) {
    const row = b.brand_payment_accounts.find(
      (r) => r?.payment_accounts || r?.payment_account
    );
    return row?.payment_accounts ?? row?.payment_account ?? null;
  }
  return null;
};

const resolveBrandCategory = (b) => {
  if (!b || typeof b !== 'object') return null;
  if (b.brand_category && typeof b.brand_category === 'object') return b.brand_category;
  if (b.brandCategory && typeof b.brandCategory === 'object') return b.brandCategory;
  return null;
};

const CreateBrand = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [url, setUrl] = useState('');
  const [address, setAddress] = useState('');
  const [invoiceLimit, setInvoiceLimit] = useState('');
  const { errors, setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors();
  const [fetchBrandAccounts] = useLazyGetBrandAccountsQuery();
  const [fetchBrandCategories] = useLazyGetBrandCategoriesQuery();

  const accountSelect = useLazySelectOptions(fetchBrandAccounts, {
    args: { paginate: 0 },
    mapResult: (res) => {
      const arr = extractList(res);
      return (Array.isArray(arr) ? arr : []).map((a) => ({
        id: a.id,
        label: accountLabel(a),
        raw: a,
      }));
    },
  });

  const categorySelect = useLazySelectOptions(fetchBrandCategories, {
    args: { paginate: 0 },
    mapResult: (res) => {
      const arr = extractList(res);
      return (Array.isArray(arr) ? arr : []).map((c) => ({
        id: c.id,
        label: categoryLabel(c),
        raw: c,
      }));
    },
  });

  const loadAccounts = useCallback(
    () =>
      accountSelect.load().catch(() => {
        toast.error('Could not load payment accounts');
      }),
    [accountSelect]
  );

  const loadCategories = useCallback(
    () =>
      categorySelect.load().catch(() => {
        toast.warn('Could not load brand categories — check /brand-categories');
      }),
    [categorySelect]
  );

  const {
    data: brandData,
    isLoading: brandLoading,
    isError: brandError,
    error: brandErr,
  } = useGetBrandByIdQuery(
    { id: editId, params: buildBrandRelationParams() },
    { skip: !isEdit || !editId }
  );

  const [createBrand, { isLoading: creating }] = useCreateBrandMutation();
  const [updateBrand, { isLoading: updating }] = useUpdateBrandMutation();
  const submitting = creating || updating;
  const loadingPage = isEdit && brandLoading;

  useEffect(() => {
    if (brandError) {
      toast.error(brandErr?.data?.message || 'Failed to load brand');
      navigate('/brands');
    }
  }, [brandError, brandErr, navigate]);

  const brandRecord = useMemo(() => {
    if (!isEdit || !brandData) return null;
    return brandData?.data ?? brandData;
  }, [isEdit, brandData]);

  useEffect(() => {
    if (!isEdit || !brandRecord) return;
    const b = brandRecord;

    setName(b.name ?? '');
    setType(b.type ?? '');
    setUrl(b.url ?? '');
    setAddress(b.address ?? '');
    setInvoiceLimit(b.invoice_limit != null ? String(b.invoice_limit) : '');

    const nestedPaymentAccount = resolvePaymentAccount(b);
    const aid = b.payment_account_id ?? nestedPaymentAccount?.id ?? undefined;

    if (aid) {
      const opt = {
        id: aid,
        label: nestedPaymentAccount
          ? accountLabel(nestedPaymentAccount)
          : String(aid),
        raw: nestedPaymentAccount ?? {},
      };
      setSelectedAccount(opt);
      accountSelect.setOptions((prev) =>
        prev.some((o) => o.id === aid) ? prev : [...prev, opt]
      );
    } else {
      setSelectedAccount(null);
    }

    const nestedCategory = resolveBrandCategory(b);
    const cid = b.brand_category_id ?? nestedCategory?.id ?? undefined;

    if (cid) {
      const opt = {
        id: cid,
        label: nestedCategory ? categoryLabel(nestedCategory) : String(cid),
        raw: nestedCategory ?? {},
      };
      setSelectedCategory(opt);
      categorySelect.setOptions((prev) =>
        prev.some((o) => o.id === cid) ? prev : [...prev, opt]
      );
    } else {
      setSelectedCategory(null);
    }
    // Seed once when brand record loads — avoid depending on option arrays
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, brandRecord]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clientErrors = {};
    if (!selectedAccount?.id)
      clientErrors.payment_account_id = 'Payment account is required';
    if (!selectedCategory?.id)
      clientErrors.brand_category_id = 'Brand category is required';
    if (!name.trim()) clientErrors.name = 'Name is required';
    if (!type.trim()) clientErrors.type = 'Type is required';
    if (!url.trim()) clientErrors.url = 'URL is required';
    if (invoiceLimit === '' || Number.isNaN(Number(invoiceLimit)))
      clientErrors.invoice_limit = 'Enter a valid invoice limit';
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    const values = {
      payment_account_id: selectedAccount.id,
      brand_category_id: selectedCategory.id,
      name: name.trim(),
      type: type.trim(),
      url: url.trim(),
      address: address.trim(),
      invoice_limit: invoiceLimit,
      status: 1,
    };

    const fd = buildFormData(values, { isEdit });

    try {
      // No relations on write requests — the page navigates away from the response
      if (isEdit) {
        await updateBrand({ id: editId, data: fd }).unwrap();
        toast.success('Brand updated');
      } else {
        await createBrand(fd).unwrap();
        toast.success('Brand added');
      }
      navigate('/brands');
    } catch (err) {
      applyApiError(err, isEdit ? 'Failed to update brand' : 'Failed to add brand');
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/brands')} sx={{ mb: 2 }}>
          Back to brands
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit brand' : 'Add brand'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={accountSelect.options}
                  value={selectedAccount}
                  onChange={(_, v) => {
                    setSelectedAccount(v);
                    clearError('payment_account_id');
                  }}
                  onOpen={loadAccounts}
                  loading={accountSelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Payment account"
                      required
                      fullWidth
                      {...fieldProps('payment_account_id')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={categorySelect.options}
                  value={selectedCategory}
                  onChange={(_, v) => {
                    setSelectedCategory(v);
                    clearError('brand_category_id');
                  }}
                  onOpen={loadCategories}
                  loading={categorySelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Brand category"
                      required
                      fullWidth
                      {...fieldProps('brand_category_id')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearError('name');
                  }}
                  required
                  fullWidth
                  {...fieldProps('name')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Type"
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                    clearError('type');
                  }}
                  required
                  fullWidth
                  placeholder="e.g. ppc, seo"
                  error={!!errors.type}
                  helperText={errors.type || 'Examples: ppc, seo'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="URL"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    clearError('url');
                  }}
                  required
                  fullWidth
                  type="url"
                  placeholder="https://"
                  {...fieldProps('url')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    clearError('address');
                  }}
                  fullWidth
                  multiline
                  minRows={2}
                  {...fieldProps('address')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Invoice limit"
                  value={invoiceLimit}
                  onChange={(e) => {
                    setInvoiceLimit(e.target.value);
                    clearError('invoice_limit');
                  }}
                  required
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  {...fieldProps('invoice_limit')}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? <CircularProgress size={22} /> : isEdit ? 'Save changes' : 'Add brand'}
                </Button>
                <Button type="button" variant="outlined" onClick={() => navigate('/brands')} disabled={submitting}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateBrand;
